import { createFileRoute } from "@tanstack/react-router";
import { Lightbulb } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { webPageScript } from "@/lib/webpage-jsonld";

export const Route = createFileRoute("/audit-roadmap")({
  head: () => ({
    meta: [
      { title: "Nine Steps to Your First SEO Audit | Online Tax VAT" },
      { name: "description", content: "A premium nine-step roadmap to run your first Claude AI powered SEO audit in under ten minutes." },
      { property: "og:title", content: "Nine Steps to Your First SEO Audit" },
      { property: "og:description", content: "Work through nine short steps to run your first AI-powered SEO audit." },
      { property: "og:url", content: "https://onlinetaxvat.com/audit-roadmap" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/audit-roadmap" }],
    scripts: [webPageScript({
      path: "/audit-roadmap",
      name: "Nine Steps to Your First SEO Audit",
      description: "A nine-step roadmap for running your first AI SEO audit with Claude.",
    })],
  }),
  component: AuditRoadmapPage,
});

const STEPS = [
  { title: "Open Claude AI", desc: "Go to claude.ai and sign in, or create a free account if you don't have one yet. No paid plan is required to follow this guide.", tip: "Bookmark claude.ai so you can return to run audits again later." },
  { title: "Enable Web Search", desc: "Open Claude's settings, and turn on Web Search. This lets Claude read your live site instead of relying on old training data.", tip: "Web Search must be on, or the audit will be based on guesses instead of real page content." },
  { title: "Copy Website URL", desc: "Grab the full URL of the site you want audited, including https:// at the start.", tip: "Use the homepage URL for a general audit, or a specific page for a focused review." },
  { title: "Paste URL Inside Claude", desc: "Start a new chat and paste the URL in first, so Claude has context before you send the master prompt.", tip: "Keep the URL and the prompt in the same message for best results." },
  { title: "Copy Master Prompt", desc: "Scroll to the Prompt section below and click Copy. Paste it directly under your URL in the same chat.", tip: "Don't edit the structure of the prompt — the headers keep Claude's output organized." },
  { title: "Run the Audit", desc: "Send the message. Claude will inspect meta tags, keywords, performance, technical SEO, content, internal links, and competitors.", tip: "This step typically takes under 90 seconds depending on site size." },
  { title: "Review the Audit", desc: "Read through the SEO Score and Critical Issues first — these tell you what's actually holding your rankings back.", tip: "Skim the tables before the paragraphs; they summarize the findings faster." },
  { title: "Prioritize Fixes", desc: "Use the 30-Day Action Plan to decide what to tackle first instead of trying to fix everything at once.", tip: "High-impact, low-effort fixes belong in Week 1 — start there." },
  { title: "Implement Week 1 Fixes", desc: "Work through the first week's tasks, then re-run the audit in two to three weeks to measure progress.", tip: "Track your SEO Score each time you re-run the prompt to see real improvement." },
];

function AuditRoadmapPage() {
  return (
    <SiteShell>
      <section className="relative bg-[#0a0d14] text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.12),transparent_60%)]" />
        <div className="container-page relative py-20 md:py-28">
          <header className="max-w-3xl mx-auto text-center">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Nine steps to your first audit
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/60 leading-relaxed">
              Work through these in order. Each one takes under a minute.
            </p>
          </header>

          <ol className="mt-14 grid gap-5 md:gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
            {STEPS.map((s, i) => (
              <li
                key={i}
                className="group relative rounded-2xl bg-[#111621] border border-white/[0.06] p-6 md:p-7 transition-all duration-300 hover:border-blue-500/30 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(59,130,246,0.4)]"
              >
                <div className="font-display text-4xl md:text-5xl font-bold text-blue-500 tabular-nums leading-none">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="mt-5 text-xl font-bold text-white tracking-tight">
                  {s.title}
                </h2>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">
                  {s.desc}
                </p>
                <div className="mt-5 flex items-start gap-3 rounded-xl bg-blue-500/[0.08] border border-blue-400/15 px-4 py-3">
                  <div className="flex-shrink-0 mt-0.5 grid place-items-center h-6 w-6 rounded-md bg-blue-500/15">
                    <Lightbulb className="h-3.5 w-3.5 text-blue-300" strokeWidth={2.4} />
                  </div>
                  <p className="text-xs md:text-[13px] text-blue-100/80 leading-relaxed">
                    <span className="font-semibold text-blue-200">Tip · </span>{s.tip}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </SiteShell>
  );
}
